/**
 * Behaviour for the shared site shell (theme::wop.header / theme::wop.footer):
 * search panel, mobile drawer, click-outside for <details> menus, sticky shadow.
 */

function toggleRegion(button: HTMLButtonElement, region: HTMLElement, open: boolean): void {
    region.hidden = !open;
    button.setAttribute('aria-expanded', String(open));

    const label = open ? button.dataset.labelClose : button.dataset.labelOpen;

    if (label) {
        button.setAttribute('aria-label', label);
    }
}

function initSearch(header: HTMLElement): void {
    const button = header.querySelector<HTMLButtonElement>('[data-wop-search-toggle]');
    const region = header.querySelector<HTMLElement>('#wop-header-search');

    if (!button || !region) {
        return;
    }

    button.addEventListener('click', () => {
        const open = region.hidden;
        toggleRegion(button, region, open);

        if (open) {
            region.querySelector<HTMLInputElement>('input')?.focus();
        }
    });

    region.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            toggleRegion(button, region, false);
            button.focus();
        }
    });
}

function initMegaMenu(header: HTMLElement): void {
    const button = header.querySelector<HTMLButtonElement>('[data-wop-mega-toggle]');
    const mega = header.querySelector<HTMLElement>('#wop-mega');

    if (!button || !mega) {
        return;
    }

    let closeTimer: number | undefined;

    const setOpen = (open: boolean) => {
        window.clearTimeout(closeTimer);
        button.setAttribute('aria-expanded', String(open));
        button.classList.toggle('is-open', open);
        const label = open ? button.dataset.labelClose : button.dataset.labelOpen;

        if (label) {
            button.setAttribute('aria-label', label);
        }

        document.documentElement.classList.toggle('wop-mega-open', open);

        if (open) {
            mega.hidden = false;
            // Next frame so the opening transition runs from the closed state.
            requestAnimationFrame(() => mega.classList.add('is-open'));
            mega.querySelector<HTMLElement>('.wop-mega__item')?.focus({ preventScroll: true });
        } else {
            mega.classList.remove('is-open');
            closeTimer = window.setTimeout(() => {
                mega.hidden = true;
            }, 320);
        }
    };

    button.addEventListener('click', () => setOpen(button.getAttribute('aria-expanded') !== 'true'));
    mega.querySelectorAll('[data-wop-mega-close]').forEach((element) => element.addEventListener('click', () => setOpen(false)));
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && button.getAttribute('aria-expanded') === 'true') {
            setOpen(false);
            button.focus();
        }
    });
}

function initDismissableMenus(): void {
    const menus = Array.from(document.querySelectorAll<HTMLDetailsElement>('details[data-wop-dismissable]'));

    document.addEventListener('pointerdown', (event) => {
        for (const menu of menus) {
            if (menu.open && event.target instanceof Node && !menu.contains(event.target)) {
                menu.open = false;
            }
        }
    });
    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') {
            return;
        }

        for (const menu of menus) {
            if (menu.open) {
                menu.open = false;
                menu.querySelector('summary')?.focus();
            }
        }
    });
}

function initStickyShadow(header: HTMLElement): void {
    const update = () => header.classList.toggle('is-scrolled', window.scrollY > 4);
    update();
    window.addEventListener('scroll', update, { passive: true });
}

export function initShell(): void {
    const header = document.querySelector<HTMLElement>('[data-wop-header]');

    if (header) {
        initSearch(header);
        initMegaMenu(header);
        initStickyShadow(header);
    }

    initDismissableMenus();
}
